/* eslint-disable react/jsx-pascal-case */
import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import NavBar from "../../../components/navbar/navbar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from '@mui/icons-material/Save';
import swal from "sweetalert";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { Banner, BannersByClassification, BannerUpdateResponse } from "./types";
import { ModalContainer } from "../styles";
import { VisuallyHiddenInput } from "./styles";
import { API_URL, sentActionLog, verify } from "../../../utils/common";
import { catalog } from "../../../models";

const Banners: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [data, setData] = React.useState<Banner[]>([]);
  const [rowCount, setRowCount] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [open, setOpen] = React.useState<boolean>(false);
  const [classifications, setClassifications] = React.useState<catalog[]>([]);
  const [selectedClassification, setSelectedClassification] = React.useState<string>("");
  const [rowSelection, setRowSelection] = React.useState<MRT_RowSelectionState>({});
  const [file, setFile] = React.useState<File | undefined>(undefined);
  const columns = React.useMemo<MRT_ColumnDef<Banner>[]>(
    () => [
      {
        accessorKey: "fileName",
        header: "Nombre de Archivo",
      },
      {
        accessorKey: "url",
        header: "URL",
      },
    ],
    []
  );

  const getBanners = React.useCallback(() => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/files/downloads/banners`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: Banner[]) => {
        setIsLoading(false);
        setData(response);
        setRowCount(response.length);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  const getClassifications = () => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/clasificaciones`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: catalog[]) => {
        setClassifications(response);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const getClassificationById = (id: string) => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/clasificaciones/${id}`, {
      method: "GET",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((response: BannersByClassification) => {
        if(response.banners){
          const selectedBanners: {[key: number]: boolean} = {}
          response.banners.forEach((item) => {
            let index = data.findIndex((banner) => banner.fileName === item.fileName)
            if(index !== -1){
              selectedBanners[index] = true
            }
          });
          setRowSelection(selectedBanners);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  React.useEffect(() => {
    (async () => {
      if (!["/", "/login"].includes(location.pathname)) {
        const currentToken = localStorage.getItem("jwt");
        if (currentToken) {
          const isValid = await verify(currentToken);
          if (!isValid) {
            navigate("/");
          } else {
            const permissions = JSON.parse(
              localStorage.getItem("user") || "{}"
            );
            if (!permissions?.administrationAccess) {
              navigate("/Home");
            }
          }
        } else {
          navigate("/");
        }
      }
    })();
  }, [location.pathname, navigate]);

  React.useEffect(() => {
    getBanners();
    getClassifications();
    sentActionLog('Visit_Banners', 'Entro en la vista Banners');
  }, [getBanners]);

  const handleDelete = (row: Banner) => {
    const token = localStorage.getItem("jwt");
    fetch(`${API_URL}/files/delete/banners/${row.fileName}`, {
      method: "DELETE",
      headers: {
        Authorization: `bearer ${token}`,
        "content-type": "application/json",
      },
    })
      .then((res) => res.ok)
      .then((response: boolean) => {
        if (response) {
          swal({
            title: "Eliminado!",
            text: "Se ha eliminado correctamente! :)",
            icon: "success",
          });
          getBanners();
          sentActionLog('Delete_Banner', `Elimino un Banner: ${row.fileName}`);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleSave = () => {
    if(file?.name){
        const token = localStorage.getItem("jwt");
        const data = {
            fileName: file.name,
            metadata: {
                title: file.name.split('.')[0]
            }
        }
        fetch(`${API_URL}/files/upload/banners`, {
            method: "POST",
            body: JSON.stringify(data),
            headers: {
            Authorization: `bearer ${token}`,
            "content-type": "application/json",
            },
        })
        .then((res) => res.json())
        .then((response: {[key: string]: string}) => {
            if (response[file.name]) {
                fetch(response[file.name], {
                    method: "PUT",
                    body: file,
                    headers: {
                        "x-amz-meta-title": file.name.split('.')[0],
                    },
                })
                .then((res) => res.ok)
                .then((response: boolean) => {
                    if (response) {
                        swal({title: "Guardado!", text: "Se ha guardado correctamente el banner", icon:"success"});
                        setOpen(false);
                        getBanners();
                        sentActionLog('Create_Banner', `Creo un nuevo Banner: ${file.name}`);
                    } else {
                        swal({title: "Error :(", text: "Hubo un error favor de contactar a soporte", icon: "error"});
                    }
                })
                .catch((err) => {
                    swal({title: "Error :(", text: JSON.stringify(err), icon: "error"});
                    console.error(err);
                });
            } else {
                swal({title: "Error :(", text: "Hubo un error favor de contactar a soporte", icon: "error"});
            }
        })
        .catch((err) => {
            swal({title: "Error :(", text: JSON.stringify(err), icon: "error"});
            console.error(err);
        });
        setFile(undefined);
    } else {
        swal({title: "Ooops :(", text: "Selecciona una Imagen", icon:"warning"});
    }
  };

  const handleAssignBanners = (rows: MRT_Row<Banner>[]) => {
    if (rows.length) {
      const token = localStorage.getItem("jwt");
      const data = {
        value: classifications.find(item => item.value.toString() === selectedClassification)?.label,
        banners: rows.map(item => item.original.fileName)
      };
      fetch(`${API_URL}/clasificaciones/${selectedClassification}`, {
        method: "PUT",
        body: JSON.stringify(data),
        headers: {
          Authorization: `bearer ${token}`,
          "content-type": "application/json",
        },
      })
        .then((res) => res.json())
        .then((response: BannerUpdateResponse) => {
          if (response.id) {
            swal({
              title: "Guardado!",
              text: "Se ha guardado correctamente! :)",
              icon: "success",
            });
            getBanners();
          }
        })
        .catch((err) => {
          console.error(err);
        });
    } else {
      swal({
        title: "Ooops :(",
        text: "Por favor selecciona al menos un banner",
        icon: "warning",
      });
    }
  };

  const handleClose = () => {
    setFile(undefined);
    setOpen(false);
  }

  const handleClassificationChange = (event: SelectChangeEvent) => {
    getClassificationById(event.target.value);
    setSelectedClassification(event.target.value.toString());
  } 

  const table = useMaterialReactTable({
    columns,
    data,
    enableFilters: false,
    enableHiding: false,
    enableSorting: false,
    enableColumnActions: false,
    enablePagination: false,
    enableRowActions: true,
    manualPagination: true,
    enableRowSelection: true,
    rowCount,
    positionActionsColumn: "last",
    onRowSelectionChange: setRowSelection,
    renderToolbarInternalActions: ({ table }) => (
      <>
        <Tooltip title="Asignar Banner a Clasificación">
          <IconButton
            onClick={() => handleAssignBanners(table.getSelectedRowModel().rows)}
          >
            <SaveIcon />
          </IconButton>
        </Tooltip>
        <IconButton onClick={() => setOpen(true)}>
          <AddIcon />
        </IconButton>
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
      </>
    ),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton color="error" onClick={() => handleDelete(row.original)}>
          <DeleteIcon />
        </IconButton>
      </Box>
    ),
    state: {
      isLoading,
      rowSelection,
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
      },
    },
    muiTableBodyRowProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        "& td": {
          "&::after": {
            backgroundColor: "#f5f5f5!important",
          },
        },
        "&:hover": {
          "& td": {
            "&::after": {
              backgroundColor: "#0000004d!important",
            },
          },
        },
      },
    },
    muiTopToolbarProps: {
      sx: {
        backgroundColor: "#f5f5f5",
        "& .MuiButton-root": {
          backgroundColor: "#F70083",
        },
      },
    },
    muiSelectCheckboxProps: {
      color: "secondary",
    },
    muiSelectAllCheckboxProps: {
      color: "secondary",
    },
  });

  const renderItems = () => {
    return classifications.map((item) => (
      <MenuItem value={item.value}>{item.label}</MenuItem>
    ));
  };

  return (
    <Box width={"100%"} mb={8}>
      <NavBar />
      <IconButton onClick={() => navigate(-1)}>
        <ArrowBackIcon />
      </IconButton>
      <Card sx={{ minWidth: 275 }}>
        <CardContent>
          <FormControl style={{width: "200px"}}>
          <InputLabel id="stateSelect">Clasificación</InputLabel>
          <Select
            labelId="stateSelect"
            color="secondary"
            value={selectedClassification}
            label="Clasificación"
            onChange={handleClassificationChange}
          >
            {renderItems()}
          </Select>
          </FormControl>
          <MaterialReactTable table={table} />
        </CardContent>
      </Card>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="Create-Edit-Offer"
      >
        <ModalContainer theme={theme}>
          <Box display={"flex"} flexDirection="row-reverse">
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box p={4} pt={0} maxHeight={"380px"}>
            <Typography mb={2} variant="h5" fontWeight="bold">
              Crear Banner
            </Typography>
            <Button
              component="label"
              role={undefined}
              variant="contained"
              color="secondary"
              tabIndex={-1}
              startIcon={<CloudUploadIcon />}
            >
              Upload file
              <VisuallyHiddenInput
                type="file"
                onChange={(event) => setFile(event.target.files?.[0])}
              />
            </Button>
            {
                file ? <Typography>{file.name}</Typography> : <br/>
            }
            <Button
              style={{ marginTop: theme.spacing(2) }}
              variant="contained"
              color="secondary"
              onClick={handleSave}
            >
              Guardar
            </Button>
          </Box>
        </ModalContainer>
      </Modal>
    </Box>
  );
};

export default Banners;
